import React, { useState } from 'react';
import { ShieldCheck, Shield, ChevronRight } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { setPatientDetails } from '../../redux/slices/appointmentSlice';
import { InsuranceModal } from './InsuranceModal';

export const PaymentInformation: React.FC = () => {
  const dispatch = useDispatch();
  const patientDetails = useSelector((state: RootState) => state.appointment.patientDetails);
  const insuranceSetting = useSelector((state: RootState) => state.auth.practice?.insurance_setting);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handlePaymentTypeChange = (type: 'insurance' | 'myself') => {
    dispatch(setPatientDetails({ ...patientDetails, paymentType: type }));
  };

  // Calculate required fields and completed count
  const getFieldsStatus = () => {
    const insDetails = patientDetails.insuranceDetails;

    // Always required: Provider, Policy Holder Name
    let totalRequired = 2;
    let completed = 0;

    if (insDetails?.provider?.trim()) completed++;
    if (insDetails?.policyHolderName?.trim()) completed++;

    // Conditionally required based on backend settings
    const showDob = insuranceSetting?.display_policy_holder_dob ?? true;
    const requireDob = insuranceSetting?.require_policy_holder_dob ?? false;
    const showMemberId = insuranceSetting?.display_member_id ?? true;
    const requireMemberId = insuranceSetting?.require_member_id ?? false;

    if (showDob && requireDob) {
      totalRequired++;
      if (insDetails?.policyHolderDob) completed++;
    }

    if (showMemberId && requireMemberId) {
      totalRequired++;
      if (insDetails?.memberId?.trim()) completed++;
    }

    return { completed, total: totalRequired };
  };

  const fieldsStatus = getFieldsStatus();
  const isComplete = fieldsStatus.completed === fieldsStatus.total;

  return (
    <div className="">
      <div className="form-control">
        <label className="label">
          <span className="label-text font-semibold text-gray-500">Payment Information <span className="text-error">*</span></span>
        </label>
        <div className="flex flex-wrap gap-8 px-1">
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="radio"
              name="paymentType"
              className="radio radio-primary radio-xs"
              checked={patientDetails.paymentType === 'insurance'}
              onChange={() => handlePaymentTypeChange('insurance')}
            />
            <span className={`text-sm ${patientDetails.paymentType === 'insurance' ? 'text-gray-900 font-medium' : 'text-gray-500'} group-hover:text-gray-900 transition-colors`}>
              I have insurance
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="radio"
              name="paymentType"
              className="radio radio-primary radio-xs"
              checked={patientDetails.paymentType === 'myself'}
              onChange={() => handlePaymentTypeChange('myself')}
            />
            <span className={`text-sm ${patientDetails.paymentType === 'myself' ? 'text-gray-900 font-medium' : 'text-gray-500'} group-hover:text-gray-900 transition-colors`}>
              I will pay myself
            </span>
          </label>
        </div>
      </div>

      {patientDetails.paymentType === 'insurance' && (
        <button
          onClick={() => setIsModalOpen(true)}
          className={`
            mt-4 w-full md:w-auto
            rounded-lg p-2
            flex items-center gap-4
            shadow-sm
            transition-all duration-500 ease-out
            hover:shadow-lg
            hover:-translate-y-0.5
            hover:scale-[1.02]
            active:scale-[0.98]
            ${isComplete
              ? 'bg-gradient-to-r from-teamcare-cyan to-teamcare-cyan-dark bg-[length:200%_200%] bg-left hover:bg-right'
              : 'bg-white border border-teamcare-cyan'
            }
          `}>
          <div className={`p-2 rounded-lg text-white ${isComplete ? 'bg-white/20 ' : 'bg-teamcare-cyan'}`}>
            {isComplete ? <ShieldCheck size={24} /> : <Shield size={24} />}
          </div>
          <div className="flex-1 text-left">
            <h3 className={`font-semibold text-md leading-tight ${isComplete ? 'text-white' : 'text-gray-600'}`}>
              {isComplete ? 'Insurance Details Added' : 'Add Insurance Details'}
            </h3>
            <p className={`text-xs ${isComplete ? 'text-white/80' : 'text-gray-500'}`}>
              {isComplete ? 'Click to edit your insurance information' : 'Click to enter your insurance information'}
            </p>
          </div>
          <ChevronRight size={24} className={isComplete ? 'text-white/60' : 'text-gray-400'} />
        </button>
      )}

      <InsuranceModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        isComplete={isComplete}
      />
    </div>
  );
};
